import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link as MuiLink,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  AccountCircle as AccountCircleIcon,
  ArrowForward as ArrowForwardIcon,
  Handshake as HandshakeIcon,
  HelpOutline as HelpOutlineIcon,
  People as PeopleIcon,
  PlayCircleOutline as PlayCircleIcon,
  SmartToy as SmartToyIcon,
  Storage as StorageIcon,
  WorkOutline as WorkOutlineIcon
} from '@mui/icons-material';

import { useCustomerPortal } from '../../contexts/CustomerPortalContext';
import {
  ACCOUNT_HELP,
  ACCOUNT_HELP_ORDER,
  Audience,
  HelpBlockId,
  SectionIcon,
  audienceMatches,
  resolveLocale
} from '../../pages/customer/help/helpContent';

/**
 * AYUDA CONTEXTUAL «¿Cómo funciona?» de «Mi cuenta».
 *
 * Explica bloque a bloque lo que el cliente tiene delante (créditos,
 * empleados, usuarios, almacenamiento) y remata cada bloque con un enlace a la
 * sección de la guía global que lo desarrolla: la ayuda corta responde el
 * «¿qué estoy viendo?» y la guía el «¿por qué funciona así?». Duplicar el texto
 * largo aquí sería garantizar que uno de los dos envejece mal, así que el
 * contenido de ambos sale del MISMO módulo bilingüe (helpContent.ts).
 *
 * Es reutilizable a propósito: `blocks` decide qué se cuenta, así que sirve
 * tanto para un botón de cabecera que lo explica todo como para un icono junto
 * al título de UNA sección concreta.
 *
 *   <AccountHelpPanel />                                  // los cuatro bloques
 *   <AccountHelpPanel blocks={['credits']} variant="icon" />  // solo créditos
 */

const BLOCK_ICONS: Record<SectionIcon, React.ReactElement> = {
  start: <PlayCircleIcon fontSize="small" />,
  employee: <SmartToyIcon fontSize="small" />,
  account: <AccountCircleIcon fontSize="small" />,
  users: <PeopleIcon fontSize="small" />,
  hire: <WorkOutlineIcon fontSize="small" />,
  credits: <WalletIcon fontSize="small" />,
  storage: <StorageIcon fontSize="small" />,
  partner: <HandshakeIcon fontSize="small" />,
  faq: <HelpOutlineIcon fontSize="small" />
};

interface AccountHelpPanelProps {
  /** Bloques a explicar, en orden. Por defecto los cuatro de «Mi cuenta». */
  blocks?: HelpBlockId[];
  /** 'button' (default) = botón con rótulo · 'icon' = solo el icono, para
   *  colgarlo del título de una sección sin robarle protagonismo. */
  variant?: 'button' | 'icon';
  /** Rótulo alternativo del disparador (por defecto «¿Cómo funciona?»). */
  label?: string;
  sx?: SxProps<Theme>;
}

const AccountHelpPanel: React.FC<AccountHelpPanelProps> = ({
  blocks = ACCOUNT_HELP_ORDER,
  variant = 'button',
  label,
  sx
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { managedByReseller, loading } = useCustomerPortal();
  const managed = !!managedByReseller;
  const [open, setOpen] = useState(false);
  const loc = resolveLocale(i18n.language);

  const trigger = label || t('customer.help.howItWorks', '¿Cómo funciona?');
  const visible = blocks.map((id) => ACCOUNT_HELP[id]).filter(Boolean);

  // Igual que la guía global: mientras no sabemos si la cuenta la lleva un
  // partner (loading) solo se muestran las viñetas comunes; ya resuelto, cada
  // bloque adapta sus puntos (p. ej. créditos: «compra en Planes» vs «pídeselo
  // a tu partner»). Nunca conviven las dos frases.
  const pointVisible = (audience?: Audience) =>
    loading ? !audience || audience === 'all' : audienceMatches(audience, managed);

  // Ir a la guía global: se cierra el diálogo antes de navegar para que la
  // página de destino no aparezca detrás de un modal abierto.
  const goToGuide = (anchor?: string) => {
    setOpen(false);
    navigate(anchor ? `/customer/help#${anchor}` : '/customer/help');
  };

  return (
    <>
      {variant === 'icon' ? (
        <Tooltip title={trigger}>
          <IconButton
            size="small"
            color="primary"
            onClick={() => setOpen(true)}
            aria-label={trigger}
            sx={sx}
          >
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          size="small"
          startIcon={<HelpOutlineIcon />}
          onClick={() => setOpen(true)}
          sx={{ textTransform: 'none', fontWeight: 600, ...sx }}
        >
          {trigger}
        </Button>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography component="div" variant="h6" sx={{ fontWeight: 700 }}>
            {t('customer.help.panelTitle', '¿Cómo funciona esta pantalla?')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t(
              'customer.help.panelLead',
              'Lo que estás viendo, explicado en un minuto. Cada bloque enlaza con la guía completa del portal.'
            )}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {visible.map((block, idx) => {
            const copy = block[loc];
            const points = block.points.filter((p) => pointVisible(p.audience)).map((p) => p[loc]);
            return (
              <Box key={block.id} sx={{ mb: idx === visible.length - 1 ? 0 : 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.75 }}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main'
                    }}
                  >
                    {BLOCK_ICONS[block.icon]}
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {copy.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.65 }}>
                  {copy.lead}
                </Typography>
                <Box component="ul" sx={{ m: 0, mb: 1, pl: 2.5 }}>
                  {points.map((point) => (
                    <Box
                      component="li"
                      key={point}
                      sx={{ py: 0.35, color: 'text.secondary', fontSize: '0.875rem', lineHeight: 1.6 }}
                    >
                      {point}
                    </Box>
                  ))}
                </Box>
                <MuiLink
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => goToGuide(block.section)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}
                >
                  {t('customer.help.readMore', 'Saber más en la guía')}
                  <ArrowForwardIcon fontSize="inherit" />
                </MuiLink>
                {idx < visible.length - 1 && <Divider sx={{ mt: 2.5 }} />}
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none' }}>
            {t('common.close', 'Cerrar')}
          </Button>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={() => goToGuide()}
            sx={{ textTransform: 'none', boxShadow: 'none', fontWeight: 600 }}
          >
            {t('customer.help.fullGuide', 'Ver la guía completa')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AccountHelpPanel;
